import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Shift, Attendance } from '@bbpos/types';
import { shiftsService } from '@/services/shifts.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { useLocationStore } from '@/stores/use-location-store';

/**
 * Look up a location's short code from the cached /auth/me profile.
 * Falls back to an empty string if profile isn't hydrated yet — callers
 * should treat this as a non-blocking enrichment, never an error.
 */
function lookupLocationCode(locationId: string): string {
  const profile = useAuthStore.getState().profile;
  return profile?.locations?.find((l) => l.id === locationId)?.location_code ?? '';
}

/**
 * Tenant + user scope used to persist the active location under the same
 * key that LocationHydrator.validateAndHydrate() reads on the next app start.
 * Without this, clock-in only set the location in memory, so a reload left
 * multi-location users with no location (and ShiftHydrator stuck waiting on it).
 */
function authScope(): { tenantId?: string; userId?: string } {
  const s = useAuthStore.getState();
  return { tenantId: s.profile?.tenant?.id, userId: s.user?.sub };
}

const SHIFT_ATTENDANCE_KEY = 'bbpos_active_attendance';
const SHIFT_DEF_KEY = 'bbpos_active_shift';

interface ShiftState {
  activeAttendance: Attendance | null;
  activeShift: Shift | null;
  isClockingIn: boolean;
  isClockingOut: boolean;
  elapsedMinutes: number;

  clockIn: (shiftId: string, locationId: string, shift: Shift, openingFloat?: number) => Promise<void>;
  clockOut: (notes?: string) => Promise<Attendance>;
  loadActiveShift: (userId: string, locationId: string) => Promise<void>;
  /**
   * The open attendance, asking the server when none is loaded yet. A page
   * that must not act without a shift calls this instead of reading
   * `activeAttendance`, which is still null on a cold load until hydration.
   */
  ensureActiveShift: () => Promise<Attendance | null>;
  startElapsedTimer: () => void;
  stopElapsedTimer: () => void;
  clearShift: () => void;
}

// Module-level timer — not in Zustand state to avoid unnecessary re-renders
let elapsedTimerId: ReturnType<typeof setInterval> | null = null;

function persistShift(attendance: Attendance, shift: Shift) {
  localStorage.setItem(SHIFT_ATTENDANCE_KEY, JSON.stringify(attendance));
  localStorage.setItem(SHIFT_DEF_KEY, JSON.stringify(shift));
}

function clearPersistedShift() {
  localStorage.removeItem(SHIFT_ATTENDANCE_KEY);
  localStorage.removeItem(SHIFT_DEF_KEY);
}

function getPersistedShift(): { attendance: Attendance; shift: Shift } | null {
  try {
    const a = localStorage.getItem(SHIFT_ATTENDANCE_KEY);
    const s = localStorage.getItem(SHIFT_DEF_KEY);
    if (a && s) {
      return { attendance: JSON.parse(a), shift: JSON.parse(s) };
    }
  } catch {
    clearPersistedShift();
  }
  return null;
}

function computeElapsed(clockIn?: string): number {
  if (!clockIn) return 0;
  const diff = Date.now() - new Date(clockIn).getTime();
  return Math.max(0, Math.floor(diff / 60000));
}

// Cross-tab sync: listen for localStorage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === SHIFT_ATTENDANCE_KEY || e.key === SHIFT_DEF_KEY) {
      const persisted = getPersistedShift();
      if (persisted) {
        useShiftStore.setState({
          activeAttendance: persisted.attendance,
          activeShift: persisted.shift,
          elapsedMinutes: computeElapsed(persisted.attendance.clock_in),
        });
        useShiftStore.getState().startElapsedTimer();
      } else {
        useShiftStore.getState().stopElapsedTimer();
        useShiftStore.setState({
          activeAttendance: null,
          activeShift: null,
          elapsedMinutes: 0,
        });
      }
    }
  });
}

export const useShiftStore = create<ShiftState>()(devtools((set, get) => ({
  activeAttendance: null,
  activeShift: null,
  isClockingIn: false,
  isClockingOut: false,
  elapsedMinutes: 0,

  clockIn: async (shiftId, locationId, shift, openingFloat) => {
    // Accept the Shift object from the caller (OpenShiftPage already has it)
    // to avoid a second API call that could fail after clock-in succeeds.
    set({ isClockingIn: true });
    try {
      const attendance = await shiftsService.clockIn({
        shift_id: shiftId,
        location_id: locationId,
        ...(openingFloat !== undefined ? { opening_float: openingFloat } : {}),
      });
      persistShift(attendance, shift);
      set({
        activeAttendance: attendance,
        activeShift: shift,
        isClockingIn: false,
        elapsedMinutes: computeElapsed(attendance.clock_in),
      });
      const { tenantId, userId } = authScope();
      useLocationStore.getState().setLocation(
        attendance.location_id,
        shift.location_name ?? '',
        lookupLocationCode(attendance.location_id),
        tenantId,
        userId,
      );
      get().startElapsedTimer();
    } catch (err) {
      set({ isClockingIn: false });
      throw err;
    }
  },

  clockOut: async (notes?) => {
    const { activeAttendance } = get();
    if (!activeAttendance) throw new Error('No active shift to clock out from');

    set({ isClockingOut: true });
    try {
      const result = await shiftsService.clockOut({
        attendance_id: activeAttendance.id,
        notes,
      });
      get().stopElapsedTimer();
      clearPersistedShift();
      set({
        activeAttendance: null,
        activeShift: null,
        isClockingOut: false,
        elapsedMinutes: 0,
      });
      return result;
    } catch (err) {
      set({ isClockingOut: false });
      throw err;
    }
  },

  loadActiveShift: async (_userId, _locationId) => {
    // Authoritative: ask server for the user's open attendance row (clock_in
    // set, clock_out NULL — regardless of PRESENT/LATE status). This recovers
    // from a logout-without-clock-out where local state was wiped but the
    // server-side row is still open.
    let active: Attendance | null = null;
    try {
      active = await shiftsService.getActiveAttendance();
    } catch (err) {
      // Network/auth error — fall back to persisted value if any. Surface the
      // cause: a programming error here (e.g. a renamed service method) looked
      // exactly like "offline" and hid a broken hydration path for months.
      console.warn('[shift-store] getActiveAttendance failed; using persisted shift:', err);
      const persisted = getPersistedShift();
      if (persisted) {
        set({
          activeAttendance: persisted.attendance,
          activeShift: persisted.shift,
          elapsedMinutes: computeElapsed(persisted.attendance.clock_in),
        });
        get().startElapsedTimer();
      }
      return;
    }

    if (!active) {
      clearPersistedShift();
      get().stopElapsedTimer();
      set({ activeAttendance: null, activeShift: null, elapsedMinutes: 0 });
      return;
    }

    let shift: Shift | null = null;
    const persisted = getPersistedShift();
    if (persisted && persisted.attendance.id === active.id) {
      shift = persisted.shift;
    } else {
      try {
        shift = await shiftsService.getShift(active.shift_id);
      } catch {
        shift = null;
      }
    }
    if (!shift) {
      // We have an open attendance but couldn't resolve the shift definition
      // — still surface the attendance so the user can clock out.
      set({
        activeAttendance: active,
        activeShift: null,
        elapsedMinutes: computeElapsed(active.clock_in),
      });
      get().startElapsedTimer();
      return;
    }

    persistShift(active, shift);
    set({
      activeAttendance: active,
      activeShift: shift,
      elapsedMinutes: computeElapsed(active.clock_in),
    });
    const { tenantId, userId } = authScope();
    useLocationStore.getState().setLocation(
      active.location_id,
      shift.location_name ?? '',
      lookupLocationCode(active.location_id),
      tenantId,
      userId,
    );
    get().startElapsedTimer();
  },

  ensureActiveShift: async () => {
    const current = get().activeAttendance;
    if (current) return current;
    // loadActiveShift ignores its arguments (the server knows the user).
    try {
      await get().loadActiveShift('', '');
    } catch {
      // Nothing more to learn: report what the store holds (no shift).
    }
    return get().activeAttendance;
  },

  startElapsedTimer: () => {
    if (elapsedTimerId) clearInterval(elapsedTimerId);

    // Tick immediately so the display is accurate from the start
    const tick = () => {
      const { activeAttendance } = get();
      if (activeAttendance?.clock_in) {
        set({ elapsedMinutes: computeElapsed(activeAttendance.clock_in) });
      }
    };
    tick();

    elapsedTimerId = setInterval(tick, 60000);
  },

  stopElapsedTimer: () => {
    if (elapsedTimerId) {
      clearInterval(elapsedTimerId);
      elapsedTimerId = null;
    }
  },

  clearShift: () => {
    get().stopElapsedTimer();
    clearPersistedShift();
    useLocationStore.getState().clearLocation();
    set({
      activeAttendance: null,
      activeShift: null,
      elapsedMinutes: 0,
    });
  },
}), { name: 'shift-store' }));
